import { useMemo, useState } from 'react';
import Grid from '@mui/material/Grid2';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { Box, Button } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/en-gb';

function ExportCard({
    title,
    description,
    month,
    onMonthChange,
    downloadPath
}: {
    title: string;
    description: string;
    month: Dayjs | null;
    onMonthChange: (value: Dayjs | null) => void;
    downloadPath: string;
}) {
    const canDownload = useMemo(() => !!month, [month]);

    const onDownload = () => {
        if (!month) {
            return;
        }

        const query = new URLSearchParams({
            month: month.startOf('month').format('YYYY-MM-DD')
        });

        window.location.href = `api/InvoiceExport/${downloadPath}?${query.toString()}`;
    };

    return (
        <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
                {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {description}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <DatePicker
                    label="Month"
                    value={month}
                    onChange={onMonthChange}
                    views={['year', 'month']}
                    openTo="month"
                />
                <Button variant="contained" onClick={onDownload} disabled={!canDownload}>
                    Download CSV
                </Button>
            </Box>
        </Paper>
    );
}

export default function InvoiceExportPage() {
    const [reminderMonth, setReminderMonth] = useState<Dayjs | null>(dayjs().startOf('month'));
    const [invoiceMonth, setInvoiceMonth] = useState<Dayjs | null>(dayjs().startOf('month'));

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'en-gb'}>
            <Grid container spacing={2}>
                <Grid size={12}>
                    <Typography variant="h4" gutterBottom>
                        Invoice Exports
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Export reminder and invoice CSV files using a single selected month for each extract.
                    </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <ExportCard
                        title="Reminder Export"
                        description="Runs the reminder extract for the selected month using the first day of that month through the first day of the next month."
                        month={reminderMonth}
                        onMonthChange={setReminderMonth}
                        downloadPath="reminder.csv"
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <ExportCard
                        title="Invoice Export"
                        description="Runs the invoice extract for the selected month using the first day of that month through the first day of the next month."
                        month={invoiceMonth}
                        onMonthChange={setInvoiceMonth}
                        downloadPath="invoice.csv"
                    />
                </Grid>
            </Grid>
        </LocalizationProvider>
    );
}
